import { useState, type MouseEvent } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../context/AuthContext";
import { ApiError } from "../api/types";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { SIDEBAR_BREAKPOINT, SIDEBAR_WIDTH } from "../components/explorer/ExplorerLayout";
import { LegalNoticeDialog } from "../components/LegalNoticeDialog";
import { LogoType } from "../components/LogoType";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { colorTokens } from "../theme/tokens";

/**
 * A two-letter avatar initial from a display name (manager decision
 * 2026-09-11: the profile menu now shows a staff member's Keycloak full
 * name, for example "Alice Martin" -> "AM", one letter per word). A
 * partner's or a locally authenticated staff member's displayName is still
 * a plain identifier with no space (a member ID or a username, for example
 * "mft-exc-a6573" -> "MF", "test-staff-1" -> "TE"), so this falls back to
 * the first two letters of the whole string whenever there is only one
 * word.
 */
function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const first = words[0].match(/[a-zA-Z]/)?.[0] ?? "";
    const last = words[words.length - 1].match(/[a-zA-Z]/)?.[0] ?? "";
    if (first && last) {
      return (first + last).toUpperCase();
    }
  }
  const letters = name.replace(/[^a-zA-Z]/g, "");
  return (letters.slice(0, 2) || "?").toUpperCase();
}

/**
 * Light top bar and footer shared by every authenticated page (partner
 * and staff), styled after the file-explorer reference layout (a single
 * role tab with a teal underline, an avatar/"Welcome"/chevron cluster on
 * the right). Only ever rendered under RequireAuth (App.tsx), which
 * already redirects away when there is no session or kind is "anon", so
 * `me` is always a signed-in session here; the `if (!me...)` guard below
 * exists only to narrow the type for TypeScript, not as a real code path.
 */
export function AppShell() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [legalOpen, setLegalOpen] = useState(false);
  // Every authenticated page shares this title (manager decision); the
  // hook restores whatever title was set before (index.html's default, or
  // the login page's) once this unmounts, for example right after logout.
  useDocumentTitle("Leeft MFT - Your files");

  function openMenu(event: MouseEvent<HTMLElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }

  function handleChangePassword() {
    closeMenu();
    navigate("/change-password");
  }

  async function handleLogout() {
    closeMenu();
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (caught) {
      setLogoutError(caught instanceof ApiError ? caught.message : "Could not sign out. Please try again.");
    }
  }

  if (!me || me.kind === "anon") {
    return null;
  }

  // Inside a sharing's detail page, StaffSharingDetailPage hands us back
  // the sharings list's own URL (path + query) as router state, so the
  // "Sharings" tab returns to that exact list state (search, sort, and so
  // on) instead of always resetting to the plain list route (manager
  // decision, 2026-09-11); it stays the single, always-selected tab
  // (Tabs value={0} below) on every staff page, detail included.
  const sharingsListBackTo = (location.state as { backTo?: string } | null)?.backTo;
  const roleTab =
    me.kind === "staff"
      ? { label: "Sharings", to: sharingsListBackTo ?? "/staff/sharings" }
      : { label: "Your Files", to: "/files" };
  // `?? me.subject` is defensive: an older session cookie encoded before
  // displayName existed decodes with it empty (session.go's own doc
  // comment), so this still shows something rather than a blank name.
  const displayName = me.displayName ?? me.subject ?? "";

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          {/*
            A fixed-width block, the same SIDEBAR_WIDTH as the explorer's
            sidebar (collapsing to auto-width once it becomes a drawer, in
            sync with ExplorerLayout's own breakpoint): the role tab right
            after it then starts exactly at the main pane's left edge,
            aligned with the Upload button below it
            (files-explorer-reference.png).
          */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              // Centres, not baseline: a baseline-aligned <img> sits on
              // the text baseline and so extends upward by its full
              // height, well past the text's cap height, which is what
              // made the logo look too high (manager review).
              alignItems: "center",
              width: { xs: "auto", [SIDEBAR_BREAKPOINT]: SIDEBAR_WIDTH },
              flexShrink: 0,
            }}
          >
            <Typography
              component="span"
              sx={{
                // The Leeft brand teal (docs/design/leeft-design-tokens.md;
                // colorTokens.brand, the same #0A4145 the logotype and the
                // theme's palette.secondary.main use), not palette.primary
                // (that's the button colour, colorTokens.button, in this
                // theme) and not a hard-coded hex (manager decision,
                // 2026-09-11).
                color: colorTokens.brand,
                fontWeight: 700,
                fontSize: { xs: 16, sm: 17, md: 20 },
                lineHeight: 1,
                m: 0,
                whiteSpace: "nowrap",
              }}
            >
              Managed file transfer by
            </Typography>
            <LogoType height={27} />
          </Stack>

          <Tabs value={0} sx={{ flexGrow: 1, minHeight: 0 }}>
            <Tab
              label={roleTab.label}
              component={RouterLink}
              to={roleTab.to}
              sx={{ minHeight: 0, py: 2, fontWeight: 700 }}
            />
          </Tabs>

          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", cursor: "pointer" }}
            onClick={openMenu}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main", fontSize: "0.875rem" }}>
              {initialsFor(displayName || "?")}
            </Avatar>
            <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.2 }}>
                Welcome
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {displayName}
              </Typography>
            </Box>
            <IconButton aria-label="Account menu" size="small">
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            <MenuItem disabled>
              <ListItemText primary={displayName} secondary={me.kind === "staff" ? "Staff" : "Partner"} />
            </MenuItem>
            {me.kind === "partner" && (
              <MenuItem onClick={handleChangePassword}>
                <ListItemIcon>
                  <LockResetIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Change password" />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                closeMenu();
                setLegalOpen(true);
              }}
            >
              <ListItemIcon>
                <GavelOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Legal notice" />
            </MenuItem>
            <MenuItem onClick={() => void handleLogout()}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign out" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/*
          Manager report: a render crash in a routed page (for example
          StaffSharingSettingsTab's null-members crash) used to take the
          whole tree down with it, leaving a blank page and no way out but
          a manual reload. This boundary is scoped to the routed content
          only, so the app bar above (and its own sign-out route out) keeps
          working even when the page below it crashes.
        */}
        <ErrorBoundary homeTo={roleTab.to} homeLabel={me.kind === "staff" ? "Back to sharings" : "Back to your files"}>
          <Outlet />
        </ErrorBoundary>
      </Box>

      {/*
        No footer: the signed-in shell used to have one just for the
        "Legal notices and source code" link, now in the account menu as
        "Legal notice" instead (manager review). The anonymous login page
        keeps its own footer link, since the SFTPGo licence's
        source-code offer must stay reachable without an account.
      */}

      <LegalNoticeDialog open={legalOpen} onClose={() => setLegalOpen(false)} />

      <Snackbar open={logoutError !== null} autoHideDuration={6000} onClose={() => setLogoutError(null)}>
        <Alert severity="error" onClose={() => setLogoutError(null)}>
          {logoutError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
